import React, { useState, useEffect, useCallback } from 'react';
import { getSocietyAdminData, API_BASE_URL } from '../services/api';
import './SocietyAdminInterestConfig.css';

interface BillCategory {
  id: number;
  society_id: number;
  name: string;
  bill_type_key: string;
  is_default: number;
  is_active: number;
  display_order: number;
}

interface SocietyAdminBillCategoriesProps {
  /** When true, hides standalone page chrome (use inside Society Details). */
  embedded?: boolean;
}

const SocietyAdminBillCategories: React.FC<SocietyAdminBillCategoriesProps> = ({ embedded = false }) => {
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId;

  const [categories, setCategories] = useState<BillCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  const [newName, setNewName] = useState('');
  const [newKey, setNewKey] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');

  const fetchCategories = useCallback(async () => {
    if (!societyId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`${API_BASE_URL}/bill-categories/${societyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.success && data.data) {
        setCategories(data.data);
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to load categories' });
    } finally {
      setLoading(false);
    }
  }, [societyId]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const autoKey = (name: string) => name.trim().toLowerCase().replace(/\s+/g, '_');

  const handleAdd = async () => {
    if (!societyId || !newName.trim()) return;
    setSaving(true);
    setAlert(null);
    try {
      const token = localStorage.getItem('admin_token');
      const key = newKey.trim() || autoKey(newName);
      const resp = await fetch(`${API_BASE_URL}/bill-categories/${societyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: newName.trim(),
          bill_type_key: key,
          display_order: categories.length,
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setAlert({ type: 'success', message: 'Category added successfully' });
        setNewName('');
        setNewKey('');
        fetchCategories();
      } else {
        setAlert({ type: 'error', message: data.error || 'Failed to add category' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to add category' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cat: BillCategory) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`${API_BASE_URL}/bill-categories/item/${cat.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_active: cat.is_active ? false : true }),
      });
      const data = await resp.json();
      if (data.success) {
        fetchCategories();
      } else {
        setAlert({ type: 'error', message: data.error || 'Failed to update category' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to update category' });
    }
  };

  const handleSaveEdit = async (cat: BillCategory) => {
    if (!editName.trim()) return;
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`${API_BASE_URL}/bill-categories/item/${cat.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: editName.trim() }),
      });
      const data = await resp.json();
      if (data.success) {
        setEditingId(null);
        setEditName('');
        fetchCategories();
      } else {
        setAlert({ type: 'error', message: data.error || 'Failed to update category' });
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.message || 'Failed to update category' });
    }
  };

  const startEdit = (cat: BillCategory) => {
    setEditingId(cat.id);
    setEditName(cat.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  if (loading) {
    return <div className="ic-loading">Loading bill categories...</div>;
  }

  return (
    <div className={embedded ? 'ic-container ic-embedded' : 'ic-container'}>
      {!embedded && (
        <div className="ic-header">
          <h2>Bill Categories</h2>
          <p className="ic-subtitle">Configure the bill category types available when creating or scheduling bills</p>
        </div>
      )}

      {alert && (
        <div className={`recon-alert recon-alert-${alert.type}`}>
          {alert.message}
          <button className="recon-alert-close" onClick={() => setAlert(null)}>&times;</button>
        </div>
      )}

      <div className="ic-card">
        {/* Category list */}
        <div className="ic-form-group">
          <label className="ic-label">Current Categories</label>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 150px 80px 100px',
              gap: 8, padding: '10px 14px', background: '#f9fafb',
              fontSize: 12, fontWeight: 600, color: '#666',
            }}>
              <span>Name</span>
              <span>Key</span>
              <span>Status</span>
              <span>Actions</span>
            </div>
            {categories.map((cat) => (
              <div key={cat.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 150px 80px 100px',
                gap: 8, padding: '10px 14px', borderTop: '1px solid #f0f0f0',
                alignItems: 'center', opacity: cat.is_active ? 1 : 0.5,
              }}>
                {editingId === cat.id ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      type="text"
                      className="ic-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(cat); if (e.key === 'Escape') cancelEdit(); }}
                      style={{ padding: '6px 8px', fontSize: 13 }}
                      autoFocus
                    />
                    <button className="recon-btn recon-btn-primary recon-btn-sm" onClick={() => handleSaveEdit(cat)}>
                      Save
                    </button>
                    <button className="recon-btn recon-btn-outline recon-btn-sm" onClick={cancelEdit}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <span style={{ fontWeight: 500, color: '#222' }}>
                    {cat.name}
                    {cat.is_default ? <span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>(default)</span> : null}
                  </span>
                )}
                <span style={{ fontSize: 13, color: '#666', fontFamily: 'monospace' }}>{cat.bill_type_key}</span>
                <span style={{
                  fontSize: 11, fontWeight: 600,
                  color: cat.is_active ? '#059669' : '#dc2626',
                }}>
                  {cat.is_active ? 'Active' : 'Inactive'}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {editingId !== cat.id && (
                    <button
                      className="recon-btn recon-btn-outline recon-btn-sm"
                      onClick={() => startEdit(cat)}
                      title="Edit name"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    className={`recon-btn recon-btn-sm ${cat.is_active ? 'recon-btn-danger' : 'recon-btn-primary'}`}
                    onClick={() => handleToggleActive(cat)}
                    title={cat.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {cat.is_active ? 'Off' : 'On'}
                  </button>
                </div>
              </div>
            ))}
            {categories.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 14 }}>
                No categories configured. Default categories will be created automatically.
              </div>
            )}
          </div>
        </div>

        {/* Add new category */}
        <div className="ic-form-group" style={{ marginTop: 24 }}>
          <label className="ic-label">Add New Category</label>
          <div className="ic-form-row">
            <div>
              <input
                type="text"
                className="ic-input"
                placeholder="Category name (e.g. Parking)"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  if (!newKey || newKey === autoKey(newName)) {
                    setNewKey(autoKey(e.target.value));
                  }
                }}
              />
              <span className="ic-help">Display name shown in bill forms</span>
            </div>
            <div>
              <input
                type="text"
                className="ic-input"
                placeholder="Key (auto-generated)"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
              />
              <span className="ic-help">Internal key stored with bills</span>
            </div>
          </div>
        </div>

        <div className="ic-actions">
          <button
            className="recon-btn recon-btn-primary"
            onClick={handleAdd}
            disabled={saving || !newName.trim()}
          >
            {saving ? 'Adding...' : 'Add Category'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SocietyAdminBillCategories;
